import { Alert, Button, Divider, Stack } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Header } from './components/Header';
import { TimeForm } from './components/TimeForm';
import { VehicleTypes } from './components/VehicleTypes';
import { useTranslation } from 'react-i18next';

import AdvancedSettings from '../AdvancedSettings/AdvancedSettings';
import { SimulationServiceFactory } from '@/modules/simulation/service/SimulationServiceFactory';
import { getVehicleAmountPreferenceKey } from '@/modules/floorplanDesign/preferences';
import { FmsConfig, VehicleTypeRange } from '@/modules/simulation/helpers/customTypes';
import { VehicleRange } from '@thive/fddm-types';
import { SimulationUtil, SimulationValidationResult } from '@/modules/simulation/SimulationUtil';
import { UIBlockingLoadIndicator } from '@/modules/simulation/components/UIBlockingLoadIndicator';
import { useVersioning } from '@/modules/floorplanV2/hooks';
import { FloorplanDesignSelector } from '@/modules/floorplanDesign/store/selectors';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { FileServiceConnector } from '@/modules/api/FileApiConnector';
import { buildRevisionFileStorageBasePath } from '@/modules/common/helpers/buildRevisionFileStorageBasePath';
import { logger } from '@/modules/common/logging/logger';
import { PROJECT_FILES_ROOT_CONTAINER } from '@/modules/api/constant';

type Props = {
  onClosePanel: () => void;
};

export const EditPanel = ({ onClosePanel }: Props) => {
  const { t } = useTranslation(['errors', 'interface']);
  const simSvc = useMemo(
    () => SimulationServiceFactory.getInstance().createSimulationService(),
    [],
  );
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);
  const { finalizeRevision } = useVersioning();

  const [loading, setLoading] = useState<boolean>(false);

  const onUploadedOrderProfile = useCallback(
    (url: string) => {
      fddmSvc.getFileReferences('CUSTOM_ORDER_PROFILE').forEach((fr) => {
        fddmSvc.deleteFileReference(fr);
      });
      fddmSvc.addFileReference({
        url,
        type: 'CUSTOM_ORDER_PROFILE',
      });
      simSvc.updateDraft({ customPlaybookFileRef: url });
    },
    [simSvc, fddmSvc],
  );

  const onRemoveUploadedOrderProfile = useCallback(() => {
    fddmSvc.getFileReferences('CUSTOM_ORDER_PROFILE').forEach((fr) => {
      fddmSvc.deleteFileReference(fr);
    });
    simSvc.updateDraft({ customPlaybookFileRef: undefined });
  }, [simSvc, fddmSvc]);

  const onUploadedFmsConfig = useCallback(
    (url: string, fmsConfig: FmsConfig) => {
      fddmSvc.getFileReferences('CUSTOM_FMS_CONFIG').forEach((fr) => {
        fddmSvc.deleteFileReference(fr);
      });
      fddmSvc.addFileReference({
        url,
        type: 'CUSTOM_FMS_CONFIG',
      });
      simSvc.updateDraft({ customFmsConfig: JSON.parse(JSON.stringify(fmsConfig)) });
    },
    [simSvc, fddmSvc],
  );

  const onRemoveUploadedFmsConfig = useCallback(() => {
    fddmSvc.getFileReferences('CUSTOM_FMS_CONFIG').forEach((fr) => {
      fddmSvc.deleteFileReference(fr);
    });
    simSvc.updateDraft({ customFmsConfig: undefined });
  }, [simSvc, fddmSvc]);

  useEffect(() => {
    const customFmsConfigUrl = simSvc.getCustomFmsConfigUrl();
    if (customFmsConfigUrl) {
      const basePath = buildRevisionFileStorageBasePath();
      if (!basePath) {
        logger.error('Could not get vehicle JSON asset. basePath not defined.');
        return;
      }

      FileServiceConnector.getInstance()
        .getJsonFileByUrl(
          `${PROJECT_FILES_ROOT_CONTAINER}?filename=${basePath}/${customFmsConfigUrl}`,
        )
        .then((json) => {
          simSvc.updateDraft({ customFmsConfig: JSON.parse(JSON.stringify(json)) });
        });
    }
  }, [simSvc]);

  //
  // Component values
  //

  const { name, config } = simSvc.useDraft();
  const { vehicleRanges } = simSvc.useDraft().config;
  const customPlaybookFileRef = simSvc.getCustomOrderProfileUrl();
  const customFmsConfigFileRef = simSvc.getCustomFmsConfigUrl();

  const recommendedSimulationTime = useMemo(() => simSvc.getRecommendedSimulationTime(), [simSvc]);
  const initialVehicleTypesRanges = useMemo(() => {
    const result: VehicleTypeRange[] = [];
    vehicleRanges.forEach((vr) => {
      const maxCapacity = simSvc.getMaxCapacity(vr.vehicleType);
      if (vr.range.length === 1) {
        result.push({
          name: vr.vehicleType,
          hue: vr.hue,
          range: {
            from: vr.range[0],
            to: vr.range[0],
            step: 1,
          },
          maxCapacity,
        });
      } else {
        const step = vr.range[1] - vr.range[0];
        const from = vr.range[0];
        const to = vr.range[vr.range.length - 1];
        result.push({
          name: vr.vehicleType,
          hue: vr.hue,
          range: {
            from,
            to,
            step,
          },
          maxCapacity,
        });
      }
    });
    return result;
  }, [simSvc, vehicleRanges]);

  //
  // Component updates
  //

  const onSimulationNameChanged = useCallback(
    (name: string): void => {
      simSvc.updateDraft({ name });
    },
    [simSvc],
  );

  const onDurationChanged = useCallback(
    (duration: number): void => {
      simSvc.updateDraft({ config: { duration } });
    },
    [simSvc],
  );

  const onVehicleTypeRangesChanged = useCallback(
    (ranges: VehicleTypeRange[], changedRange: VehicleTypeRange): void => {
      const vrs: VehicleRange[] = [];
      ranges.forEach((vtRange) => {
        const vr: VehicleRange = {
          vehicleType: vtRange.name,
          hue: vtRange.hue,
          range: [] as number[],
        };
        if (vtRange.range.from === vtRange.range.to) {
          vr.range?.push(vtRange.range.to);
        } else {
          for (let i = vtRange.range.from; i <= vtRange.range.to; i += vtRange.range.step) {
            vr.range?.push(i);
          }
        }
        vrs.push(vr);
      });
      simSvc.updateDraft({ config: { vehicleRanges: vrs } });

      if (changedRange.range.from === changedRange.range.to) {
        fddmSvc.setPreference(
          getVehicleAmountPreferenceKey(changedRange.name),
          changedRange.range.to,
        );
      }
    },
    [fddmSvc, simSvc],
  );

  const isDraftValid = useCallback(
    (): SimulationValidationResult => SimulationUtil.isValid(simSvc.getDraft()),
    [simSvc],
  );

  //
  // Run the simulation
  //

  const runSimulation = useCallback(async () => {
    try {
      setLoading(true);

      const revisionIsInDraft = FloorplanDesignSelector.revisionIsInDraft(
        useFloorplanDesignStore.getState(),
      );
      if (revisionIsInDraft) {
        await finalizeRevision();
        
        // update draft with the new meta data
        simSvc.updateDraft({ 
          floorPlanRevision: { metaData: useFloorplanDesignStore.getState().metaData } 
        });
      }

      const result = await simSvc.createSimulationGroup();
      if (result) {
        onClosePanel();
      }
    } finally {
      setLoading(false);
    }
  }, [finalizeRevision, onClosePanel, simSvc]);

  const startSimulationButtonDisabled = useMemo(() => {
    if (simSvc.getDraft().customPlaybookFileRef) return false;
    return !isDraftValid().valid;
  }, [simSvc, isDraftValid]);

  const simulationError = useMemo(() => {
    const hasUploadedOrderProfile = Boolean(simSvc.getDraft().customPlaybookFileRef);
    if (hasUploadedOrderProfile) return null;

    if (!isDraftValid().valid && isDraftValid().reasonKey) {
      return t(`errors:${isDraftValid().reasonKey}`);
    }
    return null;
  }, [t, simSvc, isDraftValid]);

  const noFlowsOrOrderProfile = useMemo(() => {
    const hasUploadedOrderProfile = Boolean(simSvc.getDraft().customPlaybookFileRef);
    const hasFlows = Boolean(useFloorplanDesignStore.getState().getFlows().length);
    return !hasFlows && !hasUploadedOrderProfile;
  }, [simSvc]);

  if (loading) {
    return <UIBlockingLoadIndicator block={loading} />;
  }

  return (
    <Stack gap={1} divider={<Divider />}>
      <Header value={name} onChange={onSimulationNameChanged} onCloseClick={() => onClosePanel()} />
      <TimeForm
        value={config.duration}
        recommendedSimulationTime={recommendedSimulationTime ?? 0}
        onChange={onDurationChanged}
      />
      <VehicleTypes
        initialRanges={initialVehicleTypesRanges}
        onChange={onVehicleTypeRangesChanged}
      />
      <AdvancedSettings
        disabled={startSimulationButtonDisabled}
        customOrderProfileUrl={customPlaybookFileRef ?? undefined}
        onUploadOrderProfile={onUploadedOrderProfile}
        onRemoveUploadedOrderProfile={onRemoveUploadedOrderProfile}
        customFmsConfigUrl={customFmsConfigFileRef ?? undefined}
        onUploadFmsConfig={onUploadedFmsConfig}
        onRemoveUploadedFmsConfig={onRemoveUploadedFmsConfig}
      />
      <Stack sx={{ px: 2, my: 2 }}>
        {noFlowsOrOrderProfile && (
          <Alert severity='warning'>{t('errors:simulation.no_flows')}</Alert>
        )}
        {simulationError && <Alert severity='error'>{simulationError}</Alert>}
        <Button variant='outlined' disabled={startSimulationButtonDisabled} onClick={runSimulation}>
          {t('interface:simulation.simulation_edit.header.label')}
        </Button>
      </Stack>
    </Stack>
  );
};
